import React, { useState, useEffect } from 'react';
import { X, Save, Download, Printer } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useAgendas } from '../../hooks/useAgendas';
import { db } from '../../firebase/config';
import { collection, onSnapshot } from 'firebase/firestore';
import { exportAgendaToWord, printAgenda } from '../../utils/agendaExport';

export default function AgendaModal({ agenda, onClose }) {
  const { 
    addAgenda, 
    updateAgenda
  } = useAgendas();
  
  const { currentUser } = useAuth();

  const isDuplicate = agenda?.isDuplicate;
  const isNew = !agenda || agenda.isNew || isDuplicate;

  const [formData, setFormData] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0],
    time: '',
    locationName: '',
    locationUrl: '',
    organizer: '',
    isTemplate: false
  });

  const [attendeesList, setAttendeesList] = useState([]);
  const [currentOppsList, setCurrentOppsList] = useState([]);
  const [newOppsList, setNewOppsList] = useState([]);
  const [progressList, setProgressList] = useState([]);
  const [actionItemsList, setActionItemsList] = useState([]);
  const [keepAwareList, setKeepAwareList] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [companyLogo, setCompanyLogo] = useState("");

  useEffect(() => {
    const unsubscribeLogo = onSnapshot(collection(db, "company_logos"), (snapshot) => {
      let activeUrl = "";
      let longarmUrl = "";
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.name?.toLowerCase().includes("longarm")) {
          longarmUrl = data.url;
        }
        if (data.status === "Active" || data.active || data.isActive) {
          activeUrl = data.url;
        }
      });
      setCompanyLogo(longarmUrl || activeUrl || "https://jimini-logistics.web.app/favicon.png");
    }, (err) => {
      console.error("Error loading company logo in agenda modal:", err);
    });
    return () => unsubscribeLogo();
  }, []);

  useEffect(() => {
    if (agenda && !agenda.isNew) {
      setFormData({
        title: isDuplicate ? `${agenda.title || ''} (Copy)` : (agenda.title || ''),
        date: isDuplicate ? new Date().toISOString().split('T')[0] : (agenda.date || new Date().toISOString().split('T')[0]),
        time: agenda.time || '',
        locationName: agenda.locationName || '',
        locationUrl: agenda.locationUrl || '',
        organizer: agenda.organizer || '',
        isTemplate: isDuplicate ? false : (agenda.isTemplate || false)
      });

      setAttendeesList(agenda.attendeesList || []);
      setCurrentOppsList(agenda.currentOppsList || []);
      setNewOppsList(agenda.newOppsList || []);
      setProgressList(agenda.progressList || []);
      setActionItemsList(agenda.actionItemsList || []);
      setKeepAwareList(agenda.keepAwareList || []);
    } else {
      setFormData({
        title: '',
        date: new Date().toISOString().split('T')[0],
        time: '',
        locationName: '',
        locationUrl: '',
        organizer: currentUser?.displayName || currentUser?.email?.split('@')[0] || '',
        isTemplate: false
      });

      setAttendeesList([]);
      setCurrentOppsList([]);
      setNewOppsList([]);
      setProgressList([]);
      setActionItemsList([]);
      setKeepAwareList([]);
    }
  }, [agenda, isNew, isDuplicate, currentUser]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getAgendaExportData = () => ({
    ...formData,
    attendeesList,
    currentOppsList,
    newOppsList,
    progressList,
    actionItemsList,
    keepAwareList
  });

  const handleExportWord = () => {
    exportAgendaToWord(getAgendaExportData(), companyLogo);
  };

  const handlePrintAgenda = () => {
    printAgenda(getAgendaExportData(), companyLogo);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        attendeesList,
        currentOppsList,
        newOppsList,
        progressList,
        actionItemsList,
        keepAwareList
      };

      if (isNew) {
        await addAgenda({
          ...payload,
          attachments: []
        });
      } else {
        await updateAgenda(agenda.id, {
          ...payload
        });
      }
      onClose();
    } catch (err) {
      console.error("Failed to save agenda:", err);
      window.alert("Failed to save agenda: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
      backdropFilter: 'blur(4px)'
    }}>
      <div 
        className="card glass-card animate-scale-up" 
        style={{
          width: '1150px',
          maxWidth: '95%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          border: '1px solid var(--border-color)',
          backgroundColor: 'rgba(20, 24, 33, 0.98)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid var(--border-color)',
          backgroundColor: 'rgba(10, 12, 18, 0.4)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h3 style={{ margin: 0, color: 'var(--primary)', fontSize: '1.10rem', fontWeight: '700' }}>
              {isNew ? (isDuplicate ? 'Duplicate Agenda' : 'Create Agenda') : 'Edit Agenda'}
            </h3>
            {!isNew && (
              <>
                <button
                  type="button"
                  onClick={handlePrintAgenda}
                  className="btn btn-secondary"
                  style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem', minHeight: 'auto', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                >
                  <Printer size={12} /> Print Agenda
                </button>
                <button
                  type="button"
                  onClick={handleExportWord}
                  className="btn btn-secondary"
                  style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem', minHeight: 'auto', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                >
                  <Download size={12} /> Export to Word (.doc)
                </button>
              </>
            )}
          </div>
          <button 
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
          <form id="agendaForm" onSubmit={handleSave}>
            
            {/* Topic & Metadata */}
            <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: '1rem', marginBottom: '1rem', alignItems: 'end' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Meeting Topic / Title *</label>
                <input 
                  type="text"
                  required
                  name="title"
                  className="form-input"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g. Weekly Business Development Status Alignment"
                />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0, height: '40px' }}>
                <input 
                  type="checkbox"
                  id="isTemplate"
                  name="isTemplate"
                  checked={formData.isTemplate}
                  style={{ width: '16px', height: '16px' }}
                  onChange={(e) => setFormData({ ...formData, isTemplate: e.target.checked })}
                />
                <label htmlFor="isTemplate" style={{ margin: 0, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                  Save as Template
                </label>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.8fr 1fr', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input 
                  type="date"
                  required
                  name="date"
                  className="form-input"
                  value={formData.date}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Time</label>
                <input 
                  type="text"
                  name="time"
                  className="form-input"
                  value={formData.time}
                  onChange={handleChange}
                  placeholder="e.g. 10:00 AM EST"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Location Label</label>
                <input 
                  type="text"
                  name="locationName"
                  className="form-input"
                  value={formData.locationName}
                  onChange={handleChange}
                  placeholder="e.g. Teams Link"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Location URL (Hyperlink)</label>
                <input 
                  type="text"
                  name="locationUrl"
                  className="form-input"
                  value={formData.locationUrl}
                  onChange={handleChange}
                  placeholder="e.g. https://teams.microsoft.com/..."
                />
              </div>
              <div className="form-group">
                <label className="form-label">Organizer</label>
                <input 
                  type="text"
                  name="organizer"
                  className="form-input"
                  value={formData.organizer}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Section 1: Attendees Table */}
            <div className="card glass-card" style={{ padding: '1rem', marginBottom: '1.5rem', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h4 style={{ margin: 0, fontWeight: '700', color: 'var(--primary)', fontSize: '0.9rem', textTransform: 'uppercase' }}>Attendees</h4>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  style={{ padding: '0.2rem 0.5rem', fontSize: '0.72rem', minHeight: 'auto' }}
                  onClick={() => setAttendeesList([...attendeesList, { name: '', title: '', company: '', present: false }])}
                >
                  + Add Row
                </button>
              </div>
              {attendeesList.length === 0 ? (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', padding: '0.75rem', border: '1px dashed var(--border-color)', borderRadius: '4px', textAlign: 'center' }}>
                  No attendees listed. Click '+ Add Row' to append lines.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '28%' }}>Name</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '32%' }}>Title / Role</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '25%' }}>Company / Dept</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'center', color: 'white', backgroundColor: '#17365D', width: '10%' }}>Present</th>
                        <th style={{ width: '40px', backgroundColor: '#17365D' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendeesList.map((row, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.name || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...attendeesList];
                                next[idx] = { ...next[idx], name: e.target.value };
                                setAttendeesList(next);
                              }}
                              placeholder="Name"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.title || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...attendeesList];
                                next[idx] = { ...next[idx], title: e.target.value };
                                setAttendeesList(next);
                              }}
                              placeholder="Title / Role"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.company || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...attendeesList];
                                next[idx] = { ...next[idx], company: e.target.value };
                                setAttendeesList(next);
                              }}
                              placeholder="Company"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>
                            <input 
                              type="checkbox" 
                              checked={row.present || false}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                              onChange={(e) => {
                                const next = [...attendeesList];
                                next[idx] = { ...next[idx], present: e.target.checked };
                                setAttendeesList(next);
                              }}
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>
                            <button 
                              type="button" 
                              style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '0.25rem' }}
                              onClick={() => setAttendeesList(attendeesList.filter((_, i) => i !== idx))}
                            >
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 2: Current Opportunities Table */}
            <div className="card glass-card" style={{ padding: '1rem', marginBottom: '1.5rem', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h4 style={{ margin: 0, fontWeight: '700', color: 'var(--primary)', fontSize: '0.9rem', textTransform: 'uppercase' }}>Current Opportunities</h4>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  style={{ padding: '0.2rem 0.5rem', fontSize: '0.72rem', minHeight: 'auto' }}
                  onClick={() => setCurrentOppsList([...currentOppsList, { opportunity: '', stage: '', value: '', nextMilestone: '', owners: '' }])}
                >
                  + Add Row
                </button>
              </div>
              {currentOppsList.length === 0 ? (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', padding: '0.75rem', border: '1px dashed var(--border-color)', borderRadius: '4px', textAlign: 'center' }}>
                  No current opportunities listed. Click '+ Add Row' to append lines.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '25%' }}>Opportunity</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '15%' }}>Stage</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '15%' }}>Value</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '25%' }}>Next Milestone</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '15%' }}>Owner(s)</th>
                        <th style={{ width: '40px', backgroundColor: '#17365D' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentOppsList.map((row, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.opportunity || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...currentOppsList];
                                next[idx] = { ...next[idx], opportunity: e.target.value };
                                setCurrentOppsList(next);
                              }}
                              placeholder="Opportunity Name"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.stage || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...currentOppsList];
                                next[idx] = { ...next[idx], stage: e.target.value };
                                setCurrentOppsList(next);
                              }}
                              placeholder="e.g. Stage 1"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.value || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...currentOppsList];
                                next[idx] = { ...next[idx], value: e.target.value };
                                setCurrentOppsList(next);
                              }}
                              placeholder="Value"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.nextMilestone || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...currentOppsList];
                                next[idx] = { ...next[idx], nextMilestone: e.target.value };
                                setCurrentOppsList(next);
                              }}
                              placeholder="e.g. Proposal Submit"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.owners || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...currentOppsList];
                                next[idx] = { ...next[idx], owners: e.target.value };
                                setCurrentOppsList(next);
                              }}
                              placeholder="Owner(s)"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>
                            <button 
                              type="button" 
                              style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '0.25rem' }}
                              onClick={() => setCurrentOppsList(currentOppsList.filter((_, i) => i !== idx))}
                            >
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 3: New Opportunities Table */}
            <div className="card glass-card" style={{ padding: '1rem', marginBottom: '1.5rem', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h4 style={{ margin: 0, fontWeight: '700', color: 'var(--primary)', fontSize: '0.9rem', textTransform: 'uppercase' }}>New Opportunities</h4>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  style={{ padding: '0.2rem 0.5rem', fontSize: '0.72rem', minHeight: 'auto' }}
                  onClick={() => setNewOppsList([...newOppsList, { opportunity: '', customerAgency: '', estValue: '', qualifications: '', owners: '' }])}
                >
                  + Add Row
                </button>
              </div>
              {newOppsList.length === 0 ? (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', padding: '0.75rem', border: '1px dashed var(--border-color)', borderRadius: '4px', textAlign: 'center' }}>
                  No new opportunities listed. Click '+ Add Row' to append lines.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '25%' }}>Opportunity</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '20%' }}>Customer/Agency</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '15%' }}>Est. Value</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '20%' }}>Qualifications</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '15%' }}>Owner(s)</th>
                        <th style={{ width: '40px', backgroundColor: '#17365D' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {newOppsList.map((row, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.opportunity || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...newOppsList];
                                next[idx] = { ...next[idx], opportunity: e.target.value };
                                setNewOppsList(next);
                              }}
                              placeholder="Opportunity"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.customerAgency || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...newOppsList];
                                next[idx] = { ...next[idx], customerAgency: e.target.value };
                                setNewOppsList(next);
                              }}
                              placeholder="Agency"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.estValue || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...newOppsList];
                                next[idx] = { ...next[idx], estValue: e.target.value };
                                setNewOppsList(next);
                              }}
                              placeholder="Est. Value"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.qualifications || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...newOppsList];
                                next[idx] = { ...next[idx], qualifications: e.target.value };
                                setNewOppsList(next);
                              }}
                              placeholder="Quals"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.owners || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...newOppsList];
                                next[idx] = { ...next[idx], owners: e.target.value };
                                setNewOppsList(next);
                              }}
                              placeholder="Owner(s)"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>
                            <button 
                              type="button" 
                              style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '0.25rem' }}
                              onClick={() => setNewOppsList(newOppsList.filter((_, i) => i !== idx))}
                            >
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 4: BD Progress Table */}
            <div className="card glass-card" style={{ padding: '1rem', marginBottom: '1.5rem', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h4 style={{ margin: 0, fontWeight: '700', color: 'var(--primary)', fontSize: '0.9rem', textTransform: 'uppercase' }}>Business Development Progress</h4>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  style={{ padding: '0.2rem 0.5rem', fontSize: '0.72rem', minHeight: 'auto' }}
                  onClick={() => setProgressList([...progressList, { initiative: '', progress: '', notes: '', owners: '' }])}
                >
                  + Add Row
                </button>
              </div>
              {progressList.length === 0 ? (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', padding: '0.75rem', border: '1px dashed var(--border-color)', borderRadius: '4px', textAlign: 'center' }}>
                  No progress updates listed. Click '+ Add Row' to append lines.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '25%' }}>Initiative</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '25%' }}>Progress</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '30%' }}>Notes</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '15%' }}>Owner(s)</th>
                        <th style={{ width: '40px', backgroundColor: '#17365D' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {progressList.map((row, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.initiative || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...progressList];
                                next[idx] = { ...next[idx], initiative: e.target.value };
                                setProgressList(next);
                              }}
                              placeholder="Initiative"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.progress || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...progressList];
                                next[idx] = { ...next[idx], progress: e.target.value };
                                setProgressList(next);
                              }}
                              placeholder="Progress"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.notes || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...progressList];
                                next[idx] = { ...next[idx], notes: e.target.value };
                                setProgressList(next);
                              }}
                              placeholder="Notes"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.owners || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...progressList];
                                next[idx] = { ...next[idx], owners: e.target.value };
                                setProgressList(next);
                              }}
                              placeholder="Owner(s)"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>
                            <button 
                              type="button" 
                              style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '0.25rem' }}
                              onClick={() => setProgressList(progressList.filter((_, i) => i !== idx))}
                            >
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 5: Call to Action Items Table */}
            <div className="card glass-card" style={{ padding: '1rem', marginBottom: '1.5rem', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h4 style={{ margin: 0, fontWeight: '700', color: 'var(--primary)', fontSize: '0.9rem', textTransform: 'uppercase' }}>Call to Action</h4>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  style={{ padding: '0.2rem 0.5rem', fontSize: '0.72rem', minHeight: 'auto' }}
                  onClick={() => setActionItemsList([...actionItemsList, { task: '', assignedTo: '', dueDate: '', status: 'Not Started' }])}
                >
                  + Add Row
                </button>
              </div>
              {actionItemsList.length === 0 ? (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', padding: '0.75rem', border: '1px dashed var(--border-color)', borderRadius: '4px', textAlign: 'center' }}>
                  No action items listed. Click '+ Add Row' to append lines.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '40%' }}>Action Item</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '20%' }}>Owner</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '20%' }}>Due Date</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '15%' }}>Status</th>
                        <th style={{ width: '40px', backgroundColor: '#17365D' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {actionItemsList.map((row, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.task || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...actionItemsList];
                                next[idx] = { ...next[idx], task: e.target.value };
                                setActionItemsList(next);
                              }}
                              placeholder="Action Item"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.assignedTo || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...actionItemsList];
                                next[idx] = { ...next[idx], assignedTo: e.target.value };
                                setActionItemsList(next);
                              }}
                              placeholder="Owner"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.dueDate || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...actionItemsList];
                                next[idx] = { ...next[idx], dueDate: e.target.value };
                                setActionItemsList(next);
                              }}
                              placeholder="Due Date"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <select 
                              className="form-input" 
                              value={row.status || 'Not Started'}
                              style={{ fontSize: '0.78rem', padding: '0.3rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...actionItemsList];
                                next[idx] = { ...next[idx], status: e.target.value };
                                setActionItemsList(next);
                              }}
                            >
                              <option value="Not Started">Not Started</option>
                              <option value="In Progress">In Progress</option>
                              <option value="Completed">Completed</option>
                              <option value="Delayed">Delayed</option>
                            </select>
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>
                            <button 
                              type="button" 
                              style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '0.25rem' }}
                              onClick={() => setActionItemsList(actionItemsList.filter((_, i) => i !== idx))}
                            >
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 6: Keep Aware Table */}
            <div className="card glass-card" style={{ padding: '1rem', marginBottom: '1.5rem', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h4 style={{ margin: 0, fontWeight: '700', color: 'var(--primary)', fontSize: '0.9rem', textTransform: 'uppercase' }}>Keep Aware</h4>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  style={{ padding: '0.2rem 0.5rem', fontSize: '0.72rem', minHeight: 'auto' }}
                  onClick={() => setKeepAwareList([...keepAwareList, { item: '', notes: '', owner: '' }])}
                >
                  + Add Row
                </button>
              </div>
              {keepAwareList.length === 0 ? (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', padding: '0.75rem', border: '1px dashed var(--border-color)', borderRadius: '4px', textAlign: 'center' }}>
                  No keep aware items listed. Click '+ Add Row' to append lines.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '35%' }}>Item / Topic</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '45%' }}>Notes & Details</th>
                        <th style={{ fontSize: '0.75rem', padding: '0.5rem', textAlign: 'left', color: 'white', backgroundColor: '#17365D', width: '20%' }}>Owner / POC</th>
                        <th style={{ width: '40px', backgroundColor: '#17365D' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {keepAwareList.map((row, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.item || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...keepAwareList];
                                next[idx] = { ...next[idx], item: e.target.value };
                                setKeepAwareList(next);
                              }}
                              placeholder="Item or Watchlist Topic"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.notes || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...keepAwareList];
                                next[idx] = { ...next[idx], notes: e.target.value };
                                setKeepAwareList(next);
                              }}
                              placeholder="Notes & Details"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem' }}>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={row.owner || ''} 
                              style={{ fontSize: '0.78rem', padding: '0.35rem 0.5rem' }}
                              onChange={(e) => {
                                const next = [...keepAwareList];
                                next[idx] = { ...next[idx], owner: e.target.value };
                                setKeepAwareList(next);
                              }}
                              placeholder="Owner / Lead"
                            />
                          </td>
                          <td style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>
                            <button 
                              type="button" 
                              style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '0.25rem' }}
                              onClick={() => setKeepAwareList(keepAwareList.filter((_, i) => i !== idx))}
                              title="Delete Row"
                            >
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </form>
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid var(--border-color)',
          backgroundColor: 'rgba(10, 12, 18, 0.4)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.75rem'
        }}>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={onClose}
            style={{ padding: '0.45rem 1rem', fontSize: '0.8rem' }}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            form="agendaForm"
            className="btn btn-primary"
            style={{ padding: '0.45rem 1.25rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : <><Save size={14} /> Save Agenda</>}
          </button>
        </div>
      </div>
    </div>
  );
}
